import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { createApi } from './api/client'
import { examsApi } from './api/exams'
import { llmApi } from './api/llm'
import { brandsApi } from './api/brands'
import { mocksApi } from './api/mocks'
import { practiceApi } from './api/practice'
import { supportApi } from './api/support'
import { questionsApi } from './api/questions'
import { createMsal } from './auth'
import { Masthead } from './Masthead'
import { createAppRouter } from './routes'
import { loadConfig, type AppConfig } from './config'

type State =
  | { status: 'loading' }
  | { status: 'ready'; config: AppConfig }
  | { status: 'failed'; message: string }

export function App() {
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    let live = true
    loadConfig()
      .then((config) => {
        if (live) setState({ status: 'ready', config })
      })
      .catch((error: unknown) => {
        if (live) setState({ status: 'failed', message: String(error) })
      })
    return () => {
      live = false
    }
  }, [])

  if (state.status === 'ready') {
    return <Admin config={state.config} />
  }

  return (
    <>
      <Masthead />
      <main className="mx-auto max-w-page px-(--container-gutter) py-8">
        {state.status === 'loading' ? (
          <p>Reading configuration...</p>
        ) : (
          <p className="border-l-2 border-red-700 pl-3">
            {state.message} Copy <code>.env.example</code> to <code>.env.local</code> and fill it in.
          </p>
        )}
      </main>
    </>
  )
}

/**
 * Everything that needs the configuration, built once it has been read.
 *
 * MSAL cannot be constructed before then, and neither can the API client, which needs both the
 * API's address and the account to get a token for. One instance per configuration: building a
 * second would strand the redirect the first one is waiting for.
 *
 * The router is made here for the same reason. Its context carries what the screens need, so
 * nothing below has to reach for a module-level singleton -- and a screen can be rendered in a
 * test with a stand-in in its place.
 */
function Admin({ config }: { config: AppConfig }) {
  const instance = useMemo(() => createMsal(config), [config])
  const queries = useMemo(() => new QueryClient(), [])
  const router = useMemo(
    () => {
      // One client for the session, shared by every feature wrapper: it is the thing that
      // holds the base URL and the bearer middleware, and a second would be a second
      // token cache.
      const client = createApi(config, instance)

      return createAppRouter({
        config,
        llm: llmApi(client),
        brands: brandsApi(client),
        questions: questionsApi(client),
        exams: examsApi(client),
        mocks: mocksApi(client),
        practice: practiceApi(client),
        support: supportApi(client),
      })
    },
    [config, instance],
  )

  return (
    <MsalProvider instance={instance}>
      <QueryClientProvider client={queries}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </MsalProvider>
  )
}
